"use client";

import { Check, RotateCcw, Search, X } from "lucide-react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

import { Mark } from "@/components/services/shared/demo-parts";
import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";

import type { AdDuelCopy, AdDuelAd } from "./advertising-page-copy";

type CcAdDuelProps = {
  copy: AdDuelCopy;
};

type Side = "a" | "b";
type Round = AdDuelCopy["rounds"][number];

/** Rounds per game, and how many of them are image rounds. */
const ROUNDS_PER_GAME = 5;
const IMAGE_ROUNDS_PER_GAME = 2;

const isImageRound = (round: Round) => Boolean(round.a.image || round.b.image);

function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * One game: image and text rounds from the pool, preferring rounds the
 * visitor hasn't played yet in this visit. `random: false` gives the fixed
 * first set rendered on the server (randomised after mount).
 */
function drawGame(pool: Round[], seen: Set<string>, random: boolean): Round[] {
  const order = (items: Round[]) => (random ? shuffle(items) : items);
  const pick = (items: Round[], count: number) => {
    const fresh = order(items.filter((round) => !seen.has(round.id)));
    const replay = order(items.filter((round) => seen.has(round.id)));
    return [...fresh, ...replay].slice(0, count);
  };
  const images = pick(pool.filter(isImageRound), IMAGE_ROUNDS_PER_GAME);
  const texts = pick(
    pool.filter((round) => !isImageRound(round)),
    ROUNDS_PER_GAME - images.length,
  );
  // Text, image, text, image, text: the server set; shuffled when random.
  const mixed = [texts[0], images[0], texts[1], images[1], texts[2]].filter(Boolean) as Round[];
  return random ? shuffle(mixed) : mixed;
}

const fill = (template: string, values: Record<string, number>) =>
  template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ""));

/** One ad in the duel: a neutral card, clickable until the round is decided. */
function DuelAd({
  ad,
  side,
  channel,
  logo,
  sponsoredLabel,
  pickLabel,
  winnerLabel,
  state,
  onPick,
}: {
  ad: AdDuelAd;
  side: Side;
  channel: string;
  logo?: string;
  sponsoredLabel: string;
  pickLabel: string;
  winnerLabel: string;
  /** "open" before a pick, then "winner" / "loser"; `picked` marks the visitor's choice. */
  state: { phase: "open" | "winner" | "loser"; picked: boolean };
  onPick: () => void;
}) {
  const decided = state.phase !== "open";
  return (
    <button
      type="button"
      className="cc-ad-duel__ad"
      data-phase={state.phase}
      data-picked={state.picked ? "true" : undefined}
      onClick={onPick}
      disabled={decided}
    >
      {/* Spoken first, so the button's name starts like its visible text. */}
      {decided ? null : (
        <span className="sr-only">
          {pickLabel} {side.toUpperCase()}:
        </span>
      )}
      <span className="cc-ad-duel__bar">
        <span className="cc-ad-duel__side" aria-hidden="true">
          {side.toUpperCase()}
        </span>
        <span className="cc-ad-duel__channel">
          <Mark logo={logo} />
          {channel}
        </span>
        {state.phase === "winner" ? <span className="cc-ad-duel__winner">{winnerLabel}</span> : null}
      </span>
      {ad.image ? (
        <span className="cc-ad-duel__media">
          <Image src={ad.image.src} alt={ad.image.alt} fill sizes="(min-width: 768px) 40vw, 92vw" />
        </span>
      ) : null}
      <span className="cc-ad-duel__body">
        {ad.query ? (
          <span className="cc-ad-preview__query">
            <Search size={14} strokeWidth={2} aria-hidden />
            {ad.query}
          </span>
        ) : null}
        <span className="cc-ad-preview__sponsor">
          <span>{sponsoredLabel}</span> · {ad.advertiser}
        </span>
        <span className="cc-ad-preview__headline">{ad.headline}</span>
        <span className="cc-ad-duel__text">{ad.body}</span>
        <span className="cc-ad-preview__cta">{ad.cta}</span>
      </span>
    </button>
  );
}

/**
 * "Annonsduellen": five rounds of "which ad won?", drawn from a pool of ~30
 * (two with images, three text) so return visits get new match-ups. The
 * visitor picks A or B, the winner is revealed with the result and why it
 * won, and after the last round a score with a light-hearted verdict and the
 * "annonsröntgen" button. "Spela igen" draws rounds not played yet.
 * Everything is real text in the page; results are announced politely.
 */
export function CcAdDuel({ copy }: CcAdDuelProps) {
  const seen = useRef(new Set<string>());
  const [rounds, setRounds] = useState(() => drawGame(copy.rounds, new Set(), false));
  const total = rounds.length;
  const [index, setIndex] = useState(0);
  const [picks, setPicks] = useState<(Side | null)[]>(() => Array(total).fill(null));
  const finished = index >= total;

  // A random set per visit, picked after hydration so server and client agree.
  useEffect(() => {
    setRounds(drawGame(copy.rounds, seen.current, true));
  }, [copy.rounds]);

  const score = picks.filter((pick, i) => pick && pick === rounds[i]?.winner).length;
  const verdict =
    copy.scoreTexts.find((entry) => score >= entry.min)?.text ?? copy.scoreTexts.at(-1)?.text ?? "";

  const round = rounds[Math.min(index, total - 1)];
  const picked = picks[index] ?? null;
  const correct = picked !== null && picked === round.winner;

  const pick = (side: Side) => {
    if (picked) return;
    seen.current.add(round.id);
    setPicks((current) => current.map((value, i) => (i === index ? side : value)));
  };

  const restart = () => {
    if (seen.current.size >= copy.rounds.length - ROUNDS_PER_GAME) seen.current.clear();
    const next = drawGame(copy.rounds, seen.current, true);
    setRounds(next);
    setPicks(Array(next.length).fill(null));
    setIndex(0);
  };

  const stateFor = (side: Side) => ({
    phase: picked ? (round.winner === side ? ("winner" as const) : ("loser" as const)) : ("open" as const),
    picked: picked === side,
  });

  return (
    <section
      className="cc-section cc-ad-duel"
      aria-labelledby="cc-ad-duel-title"
      data-cc-nav="dark"
      {...devSectionAttr("ad-duel")}
    >
      <div className="cc-ad-duel__frame">
        <div className="cc-container">
          <header className="cc-ad-duel__header">
            <CcSanjayaEyebrow theme="on-dark">{copy.eyebrow}</CcSanjayaEyebrow>
            <h2 id="cc-ad-duel-title" className="cc-ad-duel__title">
              {copy.title}
            </h2>
            <p className="cc-ad-duel__intro">{copy.intro}</p>
          </header>

          {finished ? (
            <div className="cc-ad-duel__score" role="status">
              <p className="cc-ad-duel__score-value">{fill(copy.scoreLabel, { n: score, total })}</p>
              <ol className="cc-ad-duel__dots" aria-hidden="true">
                {rounds.map((entry, i) => (
                  <li key={entry.id} data-correct={picks[i] === entry.winner ? "true" : "false"} />
                ))}
              </ol>
              <p className="cc-ad-duel__verdict">{verdict}</p>
              <p className="cc-ad-duel__cta-title">{copy.ctaTitle}</p>
              <div className="cc-ad-duel__actions">
                <CcButton
                  bookCallPopup
                  bookCallVariant="default"
                  label={copy.cta}
                  expand
                  expandCompact
                  className="cc-ad-duel__button"
                />
                <button type="button" className="cc-ad-duel__again" onClick={restart}>
                  <RotateCcw size={14} strokeWidth={2} aria-hidden />
                  {copy.again}
                </button>
              </div>
            </div>
          ) : (
            <div className="cc-ad-duel__round">
              <div className="cc-ad-duel__progress">
                <span>{fill(copy.roundLabel, { n: index + 1, total })}</span>
                <ol aria-hidden="true">
                  {rounds.map((entry, i) => (
                    <li
                      key={entry.id}
                      data-state={
                        i < index || (i === index && picked)
                          ? picks[i] === entry.winner
                            ? "right"
                            : "wrong"
                          : i === index
                            ? "current"
                            : undefined
                      }
                    />
                  ))}
                </ol>
              </div>

              <div className="cc-ad-duel__pair" key={round.id}>
                {(["a", "b"] as const).map((side) => (
                  <DuelAd
                    key={side}
                    ad={round[side]}
                    side={side}
                    channel={round.channel}
                    logo={round.logo}
                    sponsoredLabel={copy.sponsoredLabel}
                    pickLabel={copy.pickLabel}
                    winnerLabel={copy.winnerLabel}
                    state={stateFor(side)}
                    onPick={() => pick(side)}
                  />
                ))}
              </div>

              <div className="cc-ad-duel__reveal" aria-live="polite">
                {picked ? (
                  <div className="cc-ad-duel__answer" data-correct={correct ? "true" : "false"}>
                    <p className="cc-ad-duel__verdict-line">
                      {correct ? (
                        <Check size={16} strokeWidth={2.5} aria-hidden />
                      ) : (
                        <X size={16} strokeWidth={2.5} aria-hidden />
                      )}
                      <strong>{correct ? copy.correct : copy.wrong}</strong> {round.result}
                    </p>
                    <p className="cc-ad-duel__why">{round.why}</p>
                    <button
                      type="button"
                      className="cc-ad-duel__next"
                      onClick={() => setIndex((current) => current + 1)}
                    >
                      {index + 1 < total ? copy.next : copy.finish}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          )}

          <p className="cc-ad-duel__footnote">{copy.footnote}</p>
        </div>
      </div>
    </section>
  );
}
